import { createSelector } from '@reduxjs/toolkit';
import { marketsAdapter } from '~SpectateStore/slices/marketsSlice';
import { marketsEventsAdapter } from '~SpectateStore/slices/marketsEventsSlice';
import { selectAllUiMarkets } from '~SpectateStore/selectors/uiMarketSelectors';
import { selectSelectedMarketTab } from '~SpectateStore/selectors/eventsSelectors';
import { selectMarketHasSelections } from '~SpectateStore/selectors/selectionsSelectors';

export const {
    selectById: selectMarketById,
} = marketsAdapter.getSelectors(state => state.markets);

export const {
    selectAll: selectAllMarketsEvents,
    selectById: selectMarketEventById,
} = marketsEventsAdapter.getSelectors(state => state.marketsEvents);

const compoundId = (eventId, marketId) => `${eventId}-${marketId}`;

export const selectMarketName = (state, id) => selectMarketById(state, id)?.name;
export const selectMarketSelectionSchema = (state, id) => selectMarketById(state, id)?.selection_schema;
export const selectMarketIsTradable = (state, eventId, marketId) => Boolean(selectMarketEventById(state, compoundId(eventId, marketId)).tradable);
export const selectIsMarketCashoutRestricted = (state, id) => selectMarketById(state, id)?.restrict_cashout;
const selectGroupsForMarket = (state, id) => selectMarketById(state, id)?.group_names || [];
export const selectMarketOrder = (state, id) => selectMarketById(state, id)?.order;

export const selectMarketsIdsForEvent = createSelector(
    [
        state => state,
        selectAllMarketsEvents,
        selectAllUiMarkets,
        selectSelectedMarketTab,
        (state, eventId) => eventId,
    ],
    (state, marketsEvents, uiMarkets, selectedTab, eventId) => {
        const uiMarketsIds = uiMarkets
            .filter(uiMarket => {
                let groupContainsAnyMarket = true;
                if (selectedTab !== 'all-markets') {
                    groupContainsAnyMarket = uiMarket.market_ids.some(marketId => {
                        return selectGroupsForMarket(state, marketId).includes(selectedTab);
                    });
                }

                return groupContainsAnyMarket && uiMarket.event_id === eventId;
            });

        // Filter out the markets grouped in Goalscorer
        const goalscorerMarketsIds = uiMarkets.filter(uiMarket => uiMarket.group_id === 112)[0]?.market_ids ?? [];

        const marketsIds = marketsEvents
            .filter(marketEvent => {
                return marketEvent.event_id === eventId &&
                !goalscorerMarketsIds.includes(marketEvent.market_id) &&
                (selectedTab === 'all-markets' ||
                    selectGroupsForMarket(state, marketEvent.market_id).includes(selectedTab)
                ) && selectMarketHasSelections(state, eventId, marketEvent.market_id);
            }).map(({ market_id }) => ({
                'id': market_id,
                'order': selectMarketOrder(state, market_id),
        }));


        return [...uiMarketsIds, ...marketsIds]
            .sort((a, b) => Number(a?.order) - Number(b?.order))
            .map(({ id }) => id);
    },
);

export const selectMarketDefaultLine = (state, eventId, marketId) => {
    return selectMarketEventById(state, compoundId(eventId, marketId))?.default_line_selections;
};

export const selectEventHasMarkets = (state, eventId) => {
    return selectAllMarketsEvents(state)?.filter(({event_id}) => event_id === eventId).length > 0;
};
